import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api';
import AdminLayout from './AdminLayout';
import '../App.css';

const CreateUser = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role: user?.role === 'superadmin' ? 'user' : 'user'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Fetch users list
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        getApiUrl('api/users'),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setUsersList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        getApiUrl('api/users/create'),
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'User created successfully!' 
        });
        
        // Clear form
        setFormData({
          name: '',
          username: '',
          password: '',
          role: user?.role === 'superadmin' ? 'user' : 'user'
        });
        
        // Refresh users list
        fetchUsers();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Error creating user. Please try again.';
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (userItem) => {
    setSelectedUser(userItem);
    setShowViewModal(true);
  };

  const handleEditUser = (userItem) => {
    setEditFormData({
      id: userItem.id,
      name: userItem.name || '',
      username: userItem.username,
      role: userItem.role,
      password: ''
    });
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        getApiUrl(`api/users/${userId}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'User deleted successfully!' 
        });
        fetchUsers();
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Error deleting user' 
      });
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      
      const updateData = {
        name: editFormData.name,
        username: editFormData.username,
        role: editFormData.role
      };
      
      if (editFormData.password && editFormData.password.trim() !== '') {
        updateData.password = editFormData.password;
      }
      
      const response = await axios.put(
        getApiUrl(`api/users/${editFormData.id}`),
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: 'User updated successfully!' 
        });
        setShowEditModal(false);
        fetchUsers();
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Error updating user' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout pageTitle="Create User" pageSubtitle="Add a new user to the system">
          {/* Create User Form Card */}
          <div className="business-form-card">
            <div className="business-form-header">
              <div className="business-form-icon-wrapper">
                <i className="fi fi-rr-user-add"></i>
              </div>
              <h2 className="business-form-title">Create New User</h2>
              <p className="business-form-subtitle">Fill in the details to add a new user to the system</p>
            </div>

            {message.text && (
              <div className={`business-alert business-alert-${message.type === 'success' ? 'success' : 'error'}`}>
                <i className={`fi fi-rr-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                <span>{message.text}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="business-create-form">
              <div className="business-form-row">
                <div className="business-form-group">
                  <label htmlFor="name" className="business-form-label">
                    <i className="fi fi-rr-user"></i>
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter full name"
                    className="business-form-input"
                  />
                </div>

                <div className="business-form-group">
                  <label htmlFor="username" className="business-form-label">
                    <i className="fi fi-rr-at"></i>
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    placeholder="Enter username"
                    autoComplete="username"
                    className="business-form-input"
                  />
                </div>
              </div>

              <div className="business-form-row">
                <div className="business-form-group">
                  <label htmlFor="password" className="business-form-label">
                    <i className="fi fi-rr-lock"></i>
                    Password
                  </label>
                  <div className="business-password-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder="Enter password"
                      minLength="6"
                      autoComplete="new-password"
                      className="business-form-input"
                    />
                    <button
                      type="button"
                      className="business-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label="Toggle password visibility"
                    >
                      <i className={`fi fi-rr-${showPassword ? 'eye-crossed' : 'eye'}`}></i>
                    </button>
                  </div>
                </div>

                {user?.role === 'superadmin' && (
                  <div className="business-form-group">
                    <label htmlFor="role" className="business-form-label">
                      <i className="fi fi-rr-shield"></i>
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      required
                      className="business-form-input business-form-select"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="superadmin">Super Admin</option>
                    </select>
                  </div>
                )}

                {user?.role === 'admin' && (
                  <div className="business-form-group">
                    <label htmlFor="role" className="business-form-label">
                      <i className="fi fi-rr-shield"></i>
                      Role
                    </label>
                    <input
                      type="text"
                      id="role"
                      name="role"
                      value="user"
                      disabled
                      className="business-form-input business-form-input-disabled"
                    />
                    <small className="business-form-help">
                      Admins can only create users with 'user' role
                    </small>
                  </div>
                )}
              </div>

              <div className="business-form-actions">
                <button 
                  type="submit" 
                  className="business-btn business-btn-primary" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="business-spinner"></span>
                      <span>Creating User...</span>
                    </>
                  ) : (
                    <>
                      <span>Create User</span>
                      <i className="fi fi-rr-arrow-right"></i>
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  className="business-btn business-btn-secondary" 
                  onClick={() => navigate('/dashboard')}
                >
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </div>

          {/* Users List Card */}
          <div className="business-form-card" style={{ marginTop: '2rem' }}>
            <div className="business-form-header">
              <div>
                <h2 className="business-form-title">Users List</h2>
                <p className="business-form-subtitle">Manage all system users</p>
              </div>
              <button 
                className="business-btn business-btn-outline"
                onClick={fetchUsers}
              >
                <i className="fi fi-rr-refresh"></i>
                <span>Refresh</span>
              </button>
            </div>

            {loadingUsers ? (
              <div className="business-loading-state">
                <div className="business-spinner-large"></div>
                <p>Loading users...</p>
              </div>
            ) : usersList.length === 0 ? (
              <div className="business-empty-state">
                <i className="fi fi-rr-users"></i>
                <p>No users found</p>
              </div>
            ) : (
              <div className="business-table-container">
                <table className="business-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Role</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map((userItem) => (
                      <tr key={userItem.id}>
                        <td>{userItem.id}</td>
                        <td>{userItem.name || '-'}</td>
                        <td>{userItem.username}</td>
                        <td>
                          <span className={`business-role-badge business-role-${userItem.role}`}>
                            {userItem.role}
                          </span>
                        </td>
                        <td>{new Date(userItem.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="business-action-buttons">
                            <button 
                              className="business-action-btn business-action-view"
                              onClick={() => handleViewUser(userItem)}
                              title="View User"
                            >
                              <i className="fi fi-rr-eye"></i>
                            </button>
                            <button 
                              className="business-action-btn business-action-edit"
                              onClick={() => handleEditUser(userItem)}
                              title="Edit User"
                            >
                              <i className="fi fi-rr-edit"></i>
                            </button>
                            <button 
                              className="business-action-btn business-action-delete"
                              onClick={() => handleDeleteUser(userItem.id)}
                              title="Delete User"
                            >
                              <i className="fi fi-rr-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <div className="business-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="business-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="business-modal-header">
              <h2>User Details</h2>
              <button className="business-modal-close" onClick={() => setShowViewModal(false)}>
                <i className="fi fi-rr-cross"></i>
              </button>
            </div>
            <div className="business-modal-body">
              <div className="business-detail-item">
                <label>ID:</label>
                <span>{selectedUser.id}</span>
              </div>
              <div className="business-detail-item">
                <label>Name:</label>
                <span>{selectedUser.name || '-'}</span>
              </div>
              <div className="business-detail-item">
                <label>Username:</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="business-detail-item">
                <label>Role:</label>
                <span className={`business-role-badge business-role-${selectedUser.role}`}>
                  {selectedUser.role}
                </span>
              </div>
              <div className="business-detail-item">
                <label>Created At:</label>
                <span>{new Date(selectedUser.created_at).toLocaleString()}</span>
              </div>
            </div>
            <div className="business-modal-footer">
              <button className="business-btn business-btn-secondary" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="business-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="business-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="business-modal-header">
              <h2>Edit User</h2>
              <button className="business-modal-close" onClick={() => setShowEditModal(false)}>
                <i className="fi fi-rr-cross"></i>
              </button>
            </div>
            <div className="business-modal-body">
              {message.text && (
                <div className={`business-alert business-alert-${message.type === 'success' ? 'success' : 'error'}`}>
                  <i className={`fi fi-rr-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                  <span>{message.text}</span>
                </div>
              )}
              <form onSubmit={handleUpdateUser} className="business-create-form">
                <div className="business-form-group">
                  <label htmlFor="edit-name" className="business-form-label">
                    <i className="fi fi-rr-user"></i>
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="edit-name"
                    name="name"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    required
                    placeholder="Enter full name"
                    className="business-form-input"
                  />
                </div>
                <div className="business-form-group">
                  <label htmlFor="edit-username" className="business-form-label">
                    <i className="fi fi-rr-at"></i>
                    Username
                  </label>
                  <input
                    type="text"
                    id="edit-username"
                    name="username"
                    value={editFormData.username}
                    onChange={(e) => setEditFormData({...editFormData, username: e.target.value})}
                    required
                    placeholder="Enter username"
                    className="business-form-input"
                  />
                </div>
                <div className="business-form-group">
                  <label htmlFor="edit-password" className="business-form-label">
                    <i className="fi fi-rr-lock"></i>
                    Password
                  </label>
                  <input
                    type="password"
                    id="edit-password"
                    name="password"
                    value={editFormData.password || ''}
                    onChange={(e) => setEditFormData({...editFormData, password: e.target.value})}
                    placeholder="Leave empty to keep current password"
                    minLength="6"
                    autoComplete="new-password"
                    className="business-form-input"
                  />
                  <small className="business-form-help">
                    Leave empty if you don't want to change the password
                  </small>
                </div>
                {user?.role === 'superadmin' && (
                  <div className="business-form-group">
                    <label htmlFor="edit-role" className="business-form-label">
                      <i className="fi fi-rr-shield"></i>
                      Role
                    </label>
                    <select
                      id="edit-role"
                      name="role"
                      value={editFormData.role}
                      onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
                      required
                      className="business-form-input business-form-select"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="superadmin">Super Admin</option>
                    </select>
                  </div>
                )}
                {user?.role === 'admin' && (
                  <div className="business-form-group">
                    <label htmlFor="edit-role" className="business-form-label">
                      <i className="fi fi-rr-shield"></i>
                      Role
                    </label>
                    <input
                      type="text"
                      id="edit-role"
                      name="role"
                      value={editFormData.role}
                      disabled
                      className="business-form-input business-form-input-disabled"
                    />
                    <small className="business-form-help">
                      Admins can only edit users with 'user' role
                    </small>
                  </div>
                )}
                <div className="business-form-actions">
                  <button type="submit" className="business-btn business-btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="business-spinner"></span>
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <span>Update User</span>
                        <i className="fi fi-rr-arrow-right"></i>
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="business-btn business-btn-secondary" 
                    onClick={() => setShowEditModal(false)}
                  >
                    <span>Cancel</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CreateUser;
